import { useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, MoreVertical, MapPin, FolderOpen, Link, Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useRoutePlanner } from './hooks/useRoutePlanner';
import { useSavedRoutes } from './hooks/useSavedRoutes';
import { WaypointList } from './components/WaypointList';
import { WaypointSearch } from './components/WaypointSearch';
import { ClientChecklist } from './components/ClientChecklist';
import { RouteMap } from './components/RouteMap';
import { RouteSummary } from './components/RouteSummary';
import type { Waypoint } from './lib/types';
import { generateWaypointId } from './lib/route-url';

// Test addresses in Minsk for debugging
const TEST_MINSK_ADDRESSES: Array<{ lat: number; lon: number; label: string }> = [
  { lat: 53.9045, lon: 27.5615, label: 'пл. Независимости' },
  { lat: 53.8930, lon: 27.5475, label: 'ЖД Вокзал' },
  { lat: 53.9320, lon: 27.5821, label: 'ТЦ Галерея' },
  { lat: 53.9165, lon: 27.5510, label: 'Парк Горького' },
  { lat: 53.9080, lon: 27.5865, label: 'Площадь Победы' },
  { lat: 53.9250, lon: 27.6050, label: 'Комаровский рынок' },
  { lat: 53.9400, lon: 27.5970, label: 'ТЦ Замок' },
  { lat: 53.8785, lon: 27.5450, label: 'Аэропорт Минск-1' },
  { lat: 53.9350, lon: 27.6520, label: 'Восток' },
  { lat: 53.9460, lon: 27.6880, label: 'Уручье' },
  { lat: 53.8650, lon: 27.4750, label: 'Малиновка' },
  { lat: 53.9100, lon: 27.4350, label: 'Каменная Горка' },
  { lat: 53.9520, lon: 27.5150, label: 'Зелёный Луг' },
  { lat: 53.8880, lon: 27.6150, label: 'Шабаны' },
  { lat: 53.9280, lon: 27.5280, label: 'Академия наук' },
  { lat: 53.9000, lon: 27.5660, label: 'ТЦ Столица' },
  { lat: 53.9180, lon: 27.5950, label: 'Октябрьская' },
  { lat: 53.8950, lon: 27.5300, label: 'Пушкинская' },
];

export function RoutePlannerPage() {
  const navigate = useNavigate();
  const { state, actions } = useRoutePlanner();
  const { routes: savedRoutes, saveRoute, deleteRoute, getShareUrl } = useSavedRoutes();

  const [activeTab, setActiveTab] = useState<string>('search');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [routeName, setRouteName] = useState('');

  // Debug: 5 taps on title adds 15 random test waypoints
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTitleTap = useCallback(() => {
    tapCountRef.current++;

    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
    }

    tapTimerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 1000);

    if (tapCountRef.current >= 5) {
      tapCountRef.current = 0;
      // Shuffle and pick 15 random addresses
      const shuffled = [...TEST_MINSK_ADDRESSES].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, 15);

      const waypoints: Waypoint[] = selected.map((addr) => ({
        id: generateWaypointId(),
        lat: addr.lat,
        lon: addr.lon,
        label: addr.label,
        type: 'A' as const,
      }));

      actions.loadWaypoints(waypoints);
      toast.success('Тест: добавлено 15 точек', {
        description: 'Случайные адреса в Минске для тестирования',
      });
    }
  }, [actions]);

  // Set of client IDs already in the waypoint list
  const existingWaypointClientIds = useMemo(() => {
    return new Set(
      state.waypoints
        .filter((w) => w.clientId !== undefined)
        .map((w) => w.clientId as number)
    );
  }, [state.waypoints]);

  // Handler to add multiple waypoints from ClientChecklist
  const handleAddWaypoints = useCallback(
    (newWaypoints: Omit<Waypoint, 'id'>[]) => {
      for (const wp of newWaypoints) {
        actions.addWaypoint(wp);
      }
      toast.success(`Добавлено: ${newWaypoints.length}`, {
        description: 'точек в маршрут',
      });
    },
    [actions]
  );

  // Add waypoint with toast
  const handleAddWaypoint = useCallback(
    (waypoint: Omit<Waypoint, 'id'>) => {
      actions.addWaypoint(waypoint);
      toast.success('Точка добавлена');
    },
    [actions]
  );

  // Save route to localStorage
  const handleSaveRoute = useCallback(() => {
    if (state.waypoints.length < 2) {
      toast.error('Минимум 2 точки', {
        description: 'Добавьте больше точек для сохранения маршрута',
      });
      return;
    }

    if (!routeName.trim()) {
      toast.error('Введите название маршрута');
      return;
    }

    saveRoute(routeName.trim(), state.waypoints, state.costing);
    toast.success('Маршрут сохранён');
    setSaveDialogOpen(false);
    setRouteName('');
  }, [state.waypoints, state.costing, routeName, saveRoute]);

  // Load saved route
  const handleLoadRoute = useCallback(
    (route: (typeof savedRoutes)[0]) => {
      actions.loadWaypoints(route.waypoints, route.costing);
      toast.success('Маршрут загружен', {
        description: route.name,
      });
    },
    [actions]
  );

  // Share saved route
  const handleShareRoute = useCallback(
    async (route: (typeof savedRoutes)[0]) => {
      const url = getShareUrl(route);
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success('Ссылка скопирована');
      }
    },
    [getShareUrl]
  );

  // Delete saved route
  const handleDeleteRoute = useCallback(
    (route: (typeof savedRoutes)[0]) => {
      if (window.confirm(`Удалить маршрут "${route.name}"?`)) {
        deleteRoute(route.id);
        toast.success('Маршрут удалён');
      }
    },
    [deleteRoute]
  );

  // Clear all waypoints
  const handleClear = useCallback(() => {
    if (state.waypoints.length === 0) return;

    if (window.confirm('Очистить все точки маршрута?')) {
      actions.clearWaypoints();
      toast.success('Маршрут очищен');
    }
  }, [state.waypoints.length, actions]);

  // Add current location as first waypoint
  const handleAddCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Геолокация недоступна');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const wp: Omit<Waypoint, 'id'> = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          label: 'Моё местоположение',
          type: 'G',
        };

        // Insert at the beginning
        const newWaypoint: Waypoint = {
          ...wp,
          id: generateWaypointId(),
        };
        actions.loadWaypoints([newWaypoint, ...state.waypoints]);
        toast.success('Местоположение добавлено');
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Не удалось определить местоположение');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  }, [state.waypoints, actions]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b bg-background sticky top-0 z-20">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1
          className="text-lg font-semibold flex-1 select-none cursor-pointer"
          onClick={handleTitleTap}
        >
          Планировщик маршрутов
        </h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <FolderOpen className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {savedRoutes.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Нет сохранённых маршрутов
              </div>
            ) : (
              savedRoutes.map((route) => (
                <div
                  key={route.id}
                  className="flex items-center gap-1 px-2 py-1.5 hover:bg-accent rounded-sm"
                >
                  <button
                    className="flex-1 text-left text-sm truncate"
                    onClick={() => handleLoadRoute(route)}
                  >
                    {route.name}
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({route.waypoints.length})
                    </span>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShareRoute(route);
                    }}
                  >
                    <Link className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteRoute(route);
                    }}
                  >
                    <Trash className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClear}
          disabled={state.waypoints.length === 0}
        >
          <Trash2 className="w-5 h-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSaveDialogOpen(true)}
          disabled={state.waypoints.length < 2}
        >
          <Save className="w-5 h-5" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate('/route-planner/gps-diagnostics')}>
              <MapPin className="mr-2 h-4 w-4" />
              GPS Диагностика
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Tabs for adding waypoints */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="search" className="flex-1">
              Поиск
            </TabsTrigger>
            <TabsTrigger value="visits" className="flex-1">
              Из визитов
            </TabsTrigger>
          </TabsList>

          <TabsContent value="search">
            <WaypointSearch onAddWaypoint={handleAddWaypoint} />
          </TabsContent>

          <TabsContent value="visits">
            <ClientChecklist
              onAddWaypoints={handleAddWaypoints}
              existingWaypointClientIds={existingWaypointClientIds}
            />
          </TabsContent>
        </Tabs>

        {/* Waypoint list with GPS button */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-sm">
              Маршрут ({state.waypoints.length} точек)
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddCurrentLocation}
              className="text-xs"
            >
              + Моя геолокация
            </Button>
          </div>
          <WaypointList
            waypoints={state.waypoints}
            onReorder={actions.reorderWaypoints}
            onRemove={actions.removeWaypoint}
          />
        </div>

        {/* Map */}
        <div className="h-64 rounded-lg border overflow-hidden">
          <RouteMap
            waypoints={state.waypoints}
            routeShape={state.decodedShape}
            isLoading={state.isLoading || state.isOptimizing}
          />
        </div>

        {/* Summary and controls */}
        <RouteSummary
          routeResponse={state.routeResponse}
          waypoints={state.waypoints}
          costing={state.costing}
          isLoading={state.isLoading}
          isOptimizing={state.isOptimizing}
          onSetCosting={actions.setCosting}
          onOptimize={actions.optimizeOrder}
          onOptimizeFromStart={actions.optimizeFromStart}
        />

        {/* Error message */}
        {state.error && (
          <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-lg">
            {state.error}
          </div>
        )}
      </div>

      {/* Save dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Сохранить маршрут</DialogTitle>
            <DialogDescription>
              Дайте название маршруту для быстрого доступа
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="route-name">Название</Label>
            <Input
              id="route-name"
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
              placeholder="Маршрут на понедельник"
              className="mt-2"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSaveRoute}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
